import React from "react";

import {  Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow  } from "@mui/material";
import { MODES } from "../ConnectionStatistic";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CHART_BG_COLORS, CHART_BORDER_COLORS} from '../../../../constants/colors';
import { ConnectionStatsByMonth } from "../../../../models/connectionStatsByMonth";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels);

export interface IEvolutionByMonthProps  {
    evolution : ConnectionStatsByMonth[];
    loading: boolean;
    mode: MODES;
}

const EvolutionByMonth = ({evolution, loading, mode} : IEvolutionByMonthProps) => {
    if (mode === MODES.Table) {
        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow style={{ backgroundColor: '#f5f5f5'}}>
                            <TableCell colSpan={5} align="center">Evolution des connexions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableHead>
                        <TableRow>
                            <TableCell>Mois</TableCell>
                            <TableCell align="center">Utilisateurs uniques</TableCell>
                            <TableCell align="center">Connexions moyennes par utilisateur</TableCell>
                            <TableCell align="center">Connexions totales</TableCell>
                            <TableCell align="center">Devices uniques</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : evolution?.length ? (
                            evolution.map((statistic: ConnectionStatsByMonth, index: number) => (
                                <TableRow key={index}>
                                    <TableCell>{statistic.periodName}</TableCell>
                                    <TableCell align="center">{statistic.uniqueUsers}</TableCell>
                                    <TableCell align="center">{statistic.meanConnectionsPerUser}</TableCell>
                                    <TableCell align="center">{statistic.totalConnections}</TableCell>
                                    <TableCell align="center">{statistic.uniqueDevices}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                                    Aucune donnée	
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    } else if (mode === MODES.Chart) {
        const prepareChartData = () => {
            if (!evolution || evolution.length === 0) return null;
            
            const sortedData = [...evolution].sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.month.localeCompare(b.month);
            });
            
            const labels = sortedData.map(item => item.periodName);
            
            return {
                labels,
                datasets: [
                    {
                        label: 'Connexions totales',
                        data: sortedData.map(item => item.totalConnections),
                        borderColor: CHART_BORDER_COLORS[0],
                        backgroundColor: CHART_BG_COLORS[0],
                        tension: 0.3,
                        fill: false,
                    },
                    {
                        label: 'Utilisateurs uniques',
                        data: sortedData.map(item => item.uniqueUsers),
                        borderColor: CHART_BORDER_COLORS[1],
                        backgroundColor: CHART_BG_COLORS[1],
                        tension: 0.3,
                        fill: false,
                    },
                    {
                        label: 'Appareils uniques',
                        data: sortedData.map(item => item.uniqueDevices),
                        borderColor: CHART_BORDER_COLORS[2],
                        backgroundColor: CHART_BG_COLORS[2],
                        tension: 0.3,
                        fill: false,
                    }
                ],
            };
        };
        
        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top' as const,
                },
                tooltip: {
                    callbacks: {
                        label: function(context: any) {
                            const label = context.dataset.label || '';
                            const value = context.raw || 0;
                            return `${label}: ${value}`;
                        }
                    }
                },
                datalabels: {
                    display: false
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        precision: 0
                    },
                    title: {
                        display: true,
                        text: 'Nombre'
                    }
                },
                x: {
                    title: {
                        display: true,
                        text: 'Période'
                    }
                }
            }
        };
        
        const chartData = prepareChartData();
        
        return (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', textAlign: 'center', mb: 2 }}>
                    <h3>Évolution des connexions par mois</h3>
                </Box>
                <Box sx={{ height: 400, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {loading ? (
                        <CircularProgress />
                    ) : evolution?.length ? (
                        <Box sx={{ height: '100%', width: '100%', maxWidth: 800, position: 'relative' }}>
                            {chartData && <Line data={chartData} options={chartOptions} />}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>Aucune donnée</Box>
                    )}
                </Box>
            </Box>
        );
    }
    
    return null;
}

        
export default EvolutionByMonth;